import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getFestivalById, createFestival, updateFestival } from '../services/festivals.service';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const FESTIVAL_DRAFT_KEY = 'knowindia_dash_festival_form_new';

const loadFestivalDraft = () => {
  try {
    const saved = localStorage.getItem(FESTIVAL_DRAFT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed === 'object' && parsed.name !== undefined) {
        return {
          name: String(parsed.name ?? ''),
          month: String(parsed.month ?? ''),
          description: String(parsed.description ?? ''),
          main_states: String(parsed.main_states ?? ''),
          best_places: String(parsed.best_places ?? ''),
          image_url: String(parsed.image_url ?? '')
        };
      }
    }
  } catch (e) {
    console.error('Error loading festival draft:', e);
  }
  return null;
};

const saveFestivalDraft = (formData) => {
  try {
    localStorage.setItem(FESTIVAL_DRAFT_KEY, JSON.stringify(formData));
  } catch (e) {
    console.error('Error saving festival draft:', e);
  }
};

const clearFestivalDraft = () => {
  try {
    localStorage.removeItem(FESTIVAL_DRAFT_KEY);
  } catch (e) {
    console.error('Error clearing festival draft:', e);
  }
};

function FestivalForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    month: '',
    description: '',
    main_states: '',
    best_places: '',
    image_url: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFestival = useCallback(async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const response = await getFestivalById(id);
      const festival = response.data || response;
      
      setFormData({
        name: festival.name || '',
        month: festival.month || '',
        description: festival.description || '',
        main_states: festival.main_states || '',
        best_places: festival.best_places || '',
        image_url: festival.image_url || ''
      });
    } catch (error) {
      console.error('Error fetching festival:', error);
      toast.error('Failed to load festival');
      navigate('/festivals');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchFestival();
  }, [fetchFestival]);

  // Restore draft from localStorage on create mode (page refresh)
  useEffect(() => {
    if (!isEditMode) {
      const draft = loadFestivalDraft();
      if (draft) {
        setFormData(draft);
        toast.info('Draft restored. Your previous entries have been loaded.', { autoClose: 4000 });
      }
    }
  }, [isEditMode]);

  // Persist form to localStorage in create mode so refresh keeps data
  useEffect(() => {
    if (!isEditMode && (formData.name || formData.description || formData.month || formData.main_states || formData.best_places || formData.image_url)) {
      saveFestivalDraft(formData);
    }
  }, [isEditMode, formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Festival name is required';
    } else if (formData.name.length > 150) {
      newErrors.name = 'Festival name must not exceed 150 characters';
    }

    if (!formData.month) {
      newErrors.month = 'Month is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.main_states.trim()) {
      newErrors.main_states = 'Main states is required';
    } else if (formData.main_states.length > 255) {
      newErrors.main_states = 'Main states must not exceed 255 characters';
    }

    if (!formData.best_places.trim()) {
      newErrors.best_places = 'Best places is required';
    } else if (formData.best_places.length > 255) {
      newErrors.best_places = 'Best places must not exceed 255 characters';
    }

    if (!formData.image_url.trim()) {
      newErrors.image_url = 'Image URL is required';
    } else {
      try {
        new URL(formData.image_url);
      } catch {
        newErrors.image_url = 'Please enter a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode) {
        await updateFestival(id, formData);
        toast.success('Festival updated successfully');
      } else {
        await createFestival(formData);
        clearFestivalDraft();
        toast.success('Festival created successfully');
      }

      navigate('/festivals');
    } catch (error) {
      console.error('Error saving festival:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save festival';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-indigo-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extralight tracking-tight text-white">
            {isEditMode ? 'Edit' : 'Add'} Festival<span className="text-purple-400 font-bold">.</span>
          </h1>
          <p className="text-sm text-white/40 mt-1">
            {isEditMode ? 'Update festival information' : 'Add a new festival to the collection'}
          </p>
        </div>
        <button
          onClick={() => navigate('/festivals')}
          className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Back to Festivals
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6 space-y-6">
          
          {/* Festival Name */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Festival Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter festival name"
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${
                errors.name 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            />
            {errors.name && (
              <p className="mt-2 text-sm text-red-400">{errors.name}</p>
            )}
          </div>

          {/* Month */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Month <span className="text-red-400">*</span>
            </label>
            <select
              name="month"
              value={formData.month}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white focus:outline-none transition-all ${
                errors.month 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            >
              <option value="" className="bg-[#0a0a0f]">Select month</option>
              {MONTHS.map(month => (
                <option key={month} value={month} className="bg-[#0a0a0f]">{month}</option>
              ))}
            </select>
            {errors.month && (
              <p className="mt-2 text-sm text-red-400">{errors.month}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter festival description"
              rows={6}
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all resize-none ${
                errors.description 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            />
            {errors.description && (
              <p className="mt-2 text-sm text-red-400">{errors.description}</p>
            )}
          </div>

          {/* Main States */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Main States <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="main_states"
              value={formData.main_states}
              onChange={handleChange}
              placeholder="e.g., Gujarat, Maharashtra, Rajasthan"
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${
                errors.main_states 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            />
            <p className="mt-1 text-xs text-white/30">Separate multiple states with commas</p>
            {errors.main_states && (
              <p className="mt-2 text-sm text-red-400">{errors.main_states}</p>
            )}
          </div>

          {/* Best Places */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Best Places to Experience <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="best_places"
              value={formData.best_places}
              onChange={handleChange}
              placeholder="e.g., Varanasi, Ayodhya, Jaipur"
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${
                errors.best_places 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            />
            <p className="mt-1 text-xs text-white/30">Separate multiple places with commas</p>
            {errors.best_places && (
              <p className="mt-2 text-sm text-red-400">{errors.best_places}</p>
            )}
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">
              Image URL <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              name="image_url"
              value={formData.image_url}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              className={`w-full px-4 py-3 bg-white/[0.03] border rounded-xl text-white placeholder:text-white/30 focus:outline-none transition-all ${
                errors.image_url 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-white/10 focus:border-purple-500/50'
              }`}
            />
            <p className="mt-1 text-xs text-white/30">
              Use a <strong className="text-white/50">direct image link</strong> (URL that ends in .jpg, .png, or .webp). 
              For Pexels/Unsplash: open the photo, right-click the image → &quot;Copy image address&quot;, or use &quot;Download&quot; and then host the image elsewhere.
            </p>
            {errors.image_url && (
              <p className="mt-2 text-sm text-red-400">{errors.image_url}</p>
            )}
            
            {/* Image Preview */}
            {formData.image_url && !errors.image_url && (
              <div className="mt-4">
                <p className="text-sm text-white/40 mb-2">Image Preview</p>
                <div className="relative w-full max-w-md h-48 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover absolute inset-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.classList.remove('hidden');
                        fallback.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="absolute inset-0 hidden flex-col items-center justify-center gap-2 bg-white/5 p-4 text-center">
                    <span className="text-amber-400/90 text-sm font-medium">Could not load image</span>
                    <span className="text-white/40 text-xs max-w-xs">
                      This is often a <strong className="text-white/50">page link</strong> (e.g. pexels.com/photo/…) instead of the direct image file. Use right-click on the image → &quot;Copy image address&quot;, or a URL ending in .jpg / .png
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/festivals')}
            disabled={isSubmitting}
            className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-xl font-medium hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-500 hover:to-indigo-500 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-500/25"
          >
            {isSubmitting ? (
              <>
                <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">
                  {isEditMode ? 'save' : 'add'}
                </span>
                {isEditMode ? 'Update Festival' : 'Create Festival'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FestivalForm;
